import { useQuery } from "@tanstack/react-query";
import { apiGet } from "../lib/api";
import { REFETCH_INTERVAL_MS as REFETCH_MS } from "../lib/constants";
import {
  timeRangeKey,
  timeRangeQuery,
  type TimeRange,
} from "../lib/timeRange";
import type {
  BridgeIncident,
  LotUptime,
  ProblemLot,
} from "../types/bridgeHistory";
import type { PageResponse } from "../types/videoHistory";

/** 문제 많은 주차장 — 총 지속 내림차순, 페이지네이션. 기간은 상대(N일)/절대(from~to) 모두 지원. */
export function useTopProblemLots(range: TimeRange, page = 0, size = 20) {
  return useQuery<PageResponse<ProblemLot>>({
    queryKey: ["bridge-history", "top-problem-lots", timeRangeKey(range), page, size],
    queryFn: ({ signal }) =>
      apiGet<PageResponse<ProblemLot>>(
        `/api/bridge-history/top-problem-lots?${timeRangeQuery(range)}&page=${page}&size=${size}`,
        signal ? { signal } : undefined,
      ),
    refetchInterval: REFETCH_MS,
    placeholderData: (prev) => prev,
  });
}

/** 주차장별 일별 uptime (막대그래프). */
export function useBridgeUptime(days = 90) {
  return useQuery<LotUptime[]>({
    queryKey: ["bridge-history", "uptime", days],
    queryFn: ({ signal }) =>
      apiGet<LotUptime[]>(
        `/api/bridge-history/uptime?days=${days}`,
        signal ? { signal } : undefined,
      ),
    refetchInterval: REFETCH_MS,
    placeholderData: (prev) => prev,
  });
}

/** 최근 incident 타임라인. */
export function useBridgeIncidents(limit = 20) {
  return useQuery<BridgeIncident[]>({
    queryKey: ["bridge-history", "incidents", limit],
    queryFn: ({ signal }) =>
      apiGet<BridgeIncident[]>(
        `/api/bridge-history/incidents?limit=${limit}`,
        signal ? { signal } : undefined,
      ),
    refetchInterval: REFETCH_MS,
    placeholderData: (prev) => prev,
  });
}

/** 단일 (site, lot) 의 정체(DOWN) 이력 — 모달 상세에서 사용.
 *  lotId 는 전역 PK 라 이관 시 여러 사이트에 중복될 수 있어 siteKey 로 스코프한다(중복 행 방지). */
export function useBridgeLotIncidents(
  siteKey: string | null,
  lotId: string | null,
  limit = 50,
) {
  return useQuery<BridgeIncident[]>({
    queryKey: ["bridge-history", "lot-incidents", siteKey, lotId, limit],
    queryFn: ({ signal }) =>
      apiGet<BridgeIncident[]>(
        `/api/bridge-history/lots/${lotId}/incidents?siteKey=${encodeURIComponent(
          siteKey ?? "",
        )}&limit=${limit}`,
        signal ? { signal } : undefined,
      ),
    enabled: !!lotId && !!siteKey,
    placeholderData: (prev) => prev,
  });
}
